import React, { useState } from 'react';
import { fmtDate, fmtDay, fmtMoney, statusTag } from '../lib/format';
import { IconChevron, IconMail, IconMessage, IconPhone, IconPlus } from './PortalIcons';
import SwipeToDelete from './SwipeToDelete';

// A client's own page, laid out like Joist's Profile (built 2026-08-18 from
// Julian's screenshot): the name, the ways to reach them and the address in
// cards at the top, then Customer Snapshots — total invoiced, still owed, last
// invoice, last payment — then their estimates and their invoices.
//
// Tapping a client in the Clients tab used to do nothing at all. Everything
// about a repeat customer was spread across the Leads pipeline, the Projects
// tab and the ledger, and answering "what does this one owe me?" meant reading
// three screens and adding it up. This is that answer on one screen.
//
// The money is worked out here rather than fetched: the admin dashboard has
// already loaded every project, request and estimate, and a client's figures
// are a filter and a sum away. Nothing new is asked of the server.

// What the homeowner has been billed lives on a project's lines, and what they
// have paid on its payments — the client-facing half of the Project Statement.
// A request's own `invoice` is the TECH billing QPS, so it has no place here.
const sum = (rows, pick) => (rows || []).reduce((t, r) => t + (Number(pick(r)) || 0), 0);
// A discount and the tax are statement lines like any other, because that is
// what keeps every total in the app agreeing. They are money rather than work,
// so they are not counted among the items and they read under the total.
const isMoneyLine = (l) => ['discount', 'tax'].includes((l.trade || '').trim().toLowerCase());

const latest = (dates) => {
  const real = dates.filter(Boolean).map(d => new Date(d)).filter(d => !isNaN(d));
  return real.length ? new Date(Math.max(...real)) : null;
};

const Snapshot = ({ left, right }) => (
  <div className="cp-card cp-snap">
    {[left, right].map((cell, i) => (
      <div className="cp-snap-cell" key={i}>
        <div className="cp-snap-val">{cell.value}</div>
        <div className="cp-snap-lbl">{cell.label}</div>
      </div>
    ))}
  </div>
);

export default function ClientProfile({
  client, projects, requests, onBack, onEdit, onAddLead, onOpenEstimate, onOpenProject,
  onCreateEstimate, onCreateInvoice, onInvoiceEstimate, onPrintInvoice,
  onDeleteEstimate, onDeleteInvoice,
}) {
  const [extra, setExtra] = useState(false);
  const [drill, setDrill] = useState(null);   // 'estimates' | 'invoices'
  const [openInvoice, setOpenInvoice] = useState(null);   // one invoice, read as a document
  const [helping, setHelping] = useState(false);

  // A lead taken down by hand for a repeat customer isn't always linked to
  // their account, so the email is a second way in. Without one it is the link
  // or nothing — a name is not an identity.
  const email = (client.email || '').toLowerCase();
  const mine = (rec) => {
    const owner = rec.homeownerId?._id || rec.homeownerId;
    if (owner && String(owner) === String(client._id)) return true;
    return Boolean(email) && (rec.email || '').toLowerCase() === email;
  };

  const theirProjects = (projects || []).filter(mine);
  const theirRequests = (requests || []).filter(mine);

  const lines = theirProjects.flatMap(p => (p.lines || []).map(l => ({ ...l, project: p })));
  const payments = theirProjects.flatMap(p => p.payments || []);

  const invoiced = sum(lines, l => l.amount);
  const received = sum(payments, p => p.amount);
  const lastInvoice = latest(lines.map(l => l.invoiceDate));
  const lastPayment = latest(payments.map(p => p.date));

  // An estimate is a price that was sent. A job still being written up has no
  // date on it and nothing to show, so it doesn't count as one.
  const estimates = theirRequests
    .filter(r => r.estimate?.submittedAt || (r.estimateStatus && r.estimateStatus !== 'none'))
    .sort((a, b) => new Date(b.estimate?.submittedAt || b.createdAt) - new Date(a.estimate?.submittedAt || a.createdAt));

  // An invoice is every line raised under one number, on one project — which
  // is how it was written and how the homeowner reads it. Listing the lines
  // instead split one bill into a row per item, so a two-item invoice looked
  // like two invoices for the same money.
  const invoices = Object.values(lines.reduce((acc, l) => {
    const key = `${l.project._id}|${l.invoiceNumber || l._id}`;
    if (!acc[key]) {
      acc[key] = {
        key,
        number: l.invoiceNumber || '',
        date: l.invoiceDate,
        project: l.project,
        lines: [],
        items: 0,
        total: 0,
      };
    }
    acc[key].lines.push(l);
    if (!isMoneyLine(l)) acc[key].items += 1;
    acc[key].total += Number(l.amount) || 0;
    // The invoice is dated by the earliest line on it; they are written in one
    // go, but a line added later must not move the date the bill went out.
    if (l.invoiceDate && (!acc[key].date || new Date(l.invoiceDate) < new Date(acc[key].date))) {
      acc[key].date = l.invoiceDate;
    }
    return acc;
  }, {})).sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));

  // An estimate that has already been billed must not be billed again, so the
  // Invoice button is replaced by the invoice it became. The lines carry the
  // job they came from, which is the same fact the server refuses on.
  const invoicedRequestIds = new Set(
    lines.filter(l => l.requestId).map(l => String(l.requestId?._id || l.requestId)),
  );

  const serviceAddress = [client.address, client.address2].filter(Boolean).join(', ');
  const serviceCityLine = [client.city, client.state, client.zip].filter(Boolean).join(', ');
  const billingLines = [
    [client.billingAddress, client.billingAddress2].filter(Boolean).join(', '),
    [client.billingCity, client.billingState, client.billingZip].filter(Boolean).join(', '),
  ].filter(Boolean);

  const phones = [
    { label: 'Phone', value: client.phone },
    { label: 'Phone #2', value: client.phone2 },
  ].filter(p => p.value);

  // One invoice, read the way it was written: the client, then every item on
  // it with what it covered, then what it comes to. The same shape as the
  // estimate on purpose — a bill that looks like a different document from the
  // price they agreed to is a bill that gets questioned.
  if (openInvoice) {
    const inv = openInvoice;
    const items = inv.lines.filter(l => !isMoneyLine(l));
    const adjustments = inv.lines.filter(isMoneyLine);
    const subtotal = items.reduce((t, l) => t + (Number(l.amount) || 0), 0);

    return (
      <div className="cp">
        <div className="cp-bar">
          <button className="cp-bar-btn" onClick={() => setOpenInvoice(null)}>‹ Invoices</button>
          <span className="cp-bar-title">{inv.number ? `#${inv.number}` : 'Invoice'}</span>
          <span className="cp-bar-btn" style={{ visibility: 'hidden' }}>‹</span>
        </div>
        <div className="cp-body cp-doc">
          <div className="de-sec">Client</div>
          <div className="de-client">
            <div className="de-client-name">{client.name}</div>
            <div className="de-client-sub">
              {[inv.project.title, inv.date ? fmtDay(inv.date) : ''].filter(Boolean).join(' · ')}
            </div>
          </div>

          <div className="de-sec de-sec-split">
            <span>Description</span>
            <span className="de-sec-total">{fmtMoney(inv.total)}</span>
          </div>

          {items.map((l, i) => (
            <div className="de-line" key={l._id || i} style={{ cursor: 'default' }}>
              <div className="de-line-main">
                <div className="de-line-head"><span className="de-line-name">{l.trade}</span></div>
                {l.notes && <div className="de-line-desc">{l.notes}</div>}
              </div>
              <div className="de-line-right">
                <span className="de-line-amt">{fmtMoney(l.amount)}</span>
              </div>
            </div>
          ))}

          {adjustments.length > 0 && (
            <>
              <div className="de-sum"><span>Subtotal</span><span>{fmtMoney(subtotal)}</span></div>
              {adjustments.map((l, i) => (
                <div className="de-sum" key={l._id || `adj${i}`}>
                  <span>{l.trade}</span>
                  {/* A discount reads as money coming off — "−$50.00", not the
                      "$-50.00" a plain money format would print. */}
                  <span>{l.amount < 0 ? `−${fmtMoney(Math.abs(l.amount))}` : fmtMoney(l.amount)}</span>
                </div>
              ))}
            </>
          )}

          <div className="de-total"><span>Total</span><span>{fmtMoney(inv.total)}</span></div>

          <div className="cp-foot">
            {/* The bill as a sheet of paper — the customer's copy, printed or
                saved as a PDF through the browser's own dialog. */}
            <button className="btn btn-primary btn-lg" style={{ marginBottom: 10 }}
              onClick={() => onPrintInvoice({ ...inv, client })}>
              View &amp; Print PDF
            </button>
            {/* Payments are recorded against the project statement, which is
                also what the homeowner reads in their own portal. */}
            <button className="btn btn-ghost btn-lg" onClick={() => onOpenProject(inv.project)}>
              Open {inv.project.title}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (drill) {
    const isEstimates = drill === 'estimates';
    return (
      <div className="cp">
        <div className="cp-bar">
          <button className="cp-bar-btn" onClick={() => { setDrill(null); setOpenInvoice(null); }}>‹ Profile</button>
          <span className="cp-bar-title">{client.name}</span>
          <span className="cp-bar-btn" style={{ visibility: 'hidden' }}>‹</span>
        </div>
        <div className="cp-body">
          <h1 className="cp-title">{isEstimates ? 'Estimates' : 'Invoices'}</h1>

          <button className="btn btn-primary btn-lg cp-make"
            onClick={() => (isEstimates ? onCreateEstimate(client) : onCreateInvoice(client))}>
            {isEstimates ? 'Create Estimate' : 'Create Invoice'}
          </button>

          {isEstimates ? (
            estimates.length === 0 ? (
              <div className="cp-empty">No estimate has been sent to this client yet.</div>
            ) : estimates.map(r => {
              const billed = invoicedRequestIds.has(String(r._id));
              return (
                // Slide the row either way for Delete — the same gesture the
                // lead, client and vendor cards have always had.
                <SwipeToDelete key={r._id} onDelete={() => onDeleteEstimate(r)}>
                <div className="cp-card cp-rec-wrap">
                  <button className="cp-rec cp-rec-open" onClick={() => onOpenEstimate(r)}>
                    <div>
                      <div className="cp-rec-t">{r.items?.[0]?.title || r.serviceType || 'Estimate'}</div>
                      <div className="cp-rec-s">
                        {r.estimate?.submittedAt ? fmtDate(r.estimate.submittedAt) : 'Not sent'}
                        {r.workOrder?.number ? ` · WO ${r.workOrder.number}` : ''}
                      </div>
                    </div>
                    <div className="cp-rec-right">
                      <div className="cp-rec-amt">{fmtMoney(r.estimate?.amount)}</div>
                      {statusTag(r.status)}
                    </div>
                  </button>
                  {/* Joist's INVOICE button. The price they agreed to becomes
                      the bill, line for line, without retyping it. */}
                  <div className="cp-rec-acts">
                    {billed ? (
                      <span className="tag tag-green">Invoiced</span>
                    ) : (
                      <button className="btn btn-outline btn-sm" onClick={() => onInvoiceEstimate(client, r)}>
                        Invoice
                      </button>
                    )}
                  </div>
                </div>
                </SwipeToDelete>
              );
            })
          ) : (
            invoices.length === 0 ? (
              <div className="cp-empty">
                Nothing has been invoiced to this client yet.
              </div>
            ) : invoices.map(inv => (
              <SwipeToDelete key={inv.key} onDelete={() => onDeleteInvoice({ ...inv, client })}>
              <button className="cp-card cp-rec" onClick={() => setOpenInvoice(inv)}>
                <div>
                  <div className="cp-rec-t">{inv.number ? `#${inv.number}` : inv.project.title}</div>
                  <div className="cp-rec-s">
                    {inv.date ? fmtDay(inv.date) : 'No date'}
                    {` · ${inv.items} item${inv.items === 1 ? '' : 's'}`}
                    {inv.number ? ` · ${inv.project.title}` : ''}
                  </div>
                </div>
                <div className="cp-rec-right">
                  <div className="cp-rec-amt">{fmtMoney(inv.total)}</div>
                  <span className="cp-rec-chev">›</span>
                </div>
              </button>
              </SwipeToDelete>
            ))
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="cp">
      <div className="cp-bar">
        <button className="cp-bar-btn" onClick={onBack}>‹ Clients</button>
        <div className="cp-bar-right">
          {/* A repeat customer rings about the next job while you have their
              page open — this starts that lead with everything already filled
              in from the record you are looking at. */}
          <button className="cp-bar-icon" aria-label="New lead for this client" onClick={() => onAddLead(client)}>
            <IconPlus size={22} />
          </button>
          <button className="cp-bar-btn" onClick={() => onEdit(client)}>Edit</button>
        </div>
      </div>

      <div className="cp-body">
        <h1 className="cp-title">Profile</h1>

        <div className="cp-card">
          <div className="cp-name">{client.name}</div>

          <div className="cp-line">
            <div className="cp-line-main">
              <div className="cp-line-lbl">Email</div>
              <div className={`cp-line-val${client.email ? '' : ' empty'}`}>{client.email || 'None on file'}</div>
            </div>
            {client.email && (
              <a className="cp-act" href={`mailto:${client.email}`} aria-label="Email this client"><IconMail /></a>
            )}
          </div>

          {phones.length === 0 ? (
            <div className="cp-line">
              <div className="cp-line-main">
                <div className="cp-line-lbl">Phone</div>
                <div className="cp-line-val empty">None on file</div>
              </div>
            </div>
          ) : phones.map(p => (
            <div className="cp-line" key={p.label}>
              <div className="cp-line-main">
                <div className="cp-line-lbl">{p.label}</div>
                <div className="cp-line-val">{p.value}</div>
              </div>
              <a className="cp-act" href={`tel:${p.value}`} aria-label={`Call ${p.value}`}><IconPhone /></a>
              <a className="cp-act" href={`sms:${p.value}`} aria-label={`Text ${p.value}`}><IconMessage /></a>
            </div>
          ))}
        </div>

        <div className="cp-card">
          <div className="cp-line-lbl">Address</div>
          {serviceAddress || serviceCityLine ? (
            <>
              {serviceAddress && <div className="cp-addr">{serviceAddress}</div>}
              {serviceCityLine && <div className="cp-addr">{serviceCityLine}</div>}
            </>
          ) : (
            <div className="cp-line-val empty">None on file</div>
          )}
        </div>

        <div className="cp-h2">
          Customer Snapshots
          <button className="cp-help" aria-label="What these figures mean" onClick={() => setHelping(h => !h)}>?</button>
        </div>
        {helping && (
          <div className="cp-help-note">
            What this client has been billed across their projects, and what has come back. Estimates are not
            money — a price becomes invoiced when it goes on a project as a line.
          </div>
        )}

        <Snapshot
          left={{ value: fmtMoney(invoiced), label: 'Total invoiced' }}
          right={{ value: fmtMoney(Math.max(0, invoiced - received)), label: 'Total unpaid invoices' }}
        />
        <Snapshot
          left={{ value: lastInvoice ? fmtDay(lastInvoice) : '––', label: 'Last invoice sent' }}
          right={{ value: lastPayment ? fmtDay(lastPayment) : '––', label: 'Last payment received' }}
        />

        <div className="cp-card cp-nav">
          <button className="cp-nav-row" onClick={() => setDrill('estimates')}>
            <span>Estimates</span>
            <span className="cp-nav-right"><span className="cp-count">{estimates.length}</span><IconChevron /></span>
          </button>
          <button className="cp-nav-row" onClick={() => setDrill('invoices')}>
            <span>Invoices</span>
            <span className="cp-nav-right"><span className="cp-count">{lines.length}</span><IconChevron /></span>
          </button>
        </div>

        <div className="cp-make-row">
          <button className="btn btn-primary btn-lg" onClick={() => onCreateEstimate(client)}>Create Estimate</button>
          <button className="btn btn-ghost btn-lg" onClick={() => onCreateInvoice(client)}>Create Invoice</button>
        </div>

        {/* Joist's own switch, and worth keeping: the billing address, the gate
            code and whether they can get into a portal are things you need
            twice a year, and they push the money off the screen the rest of
            the time. */}
        <label className="cp-extra">
          <span>
            <span className="cp-extra-t">Show Additional Information</span>
            <span className="cp-extra-s">See important details about how you work with this customer.</span>
          </span>
          <span className={`dopt-switch${extra ? ' on' : ''}`}>
            <input
              type="checkbox"
              checked={extra}
              onChange={e => setExtra(e.target.checked)}
              // Visually hidden rather than display:none — Safari will not
              // toggle a display:none control from a label.
              style={{ position: 'absolute', opacity: 0, width: 1, height: 1 }}
            />
            <span className="dopt-knob"></span>
          </span>
        </label>

        {extra && (
          <div className="cp-card">
            <div className="cp-line">
              <div className="cp-line-main">
                <div className="cp-line-lbl">Billing address</div>
                {billingLines.length
                  ? billingLines.map((l, i) => <div className="cp-addr" key={i}>{l}</div>)
                  : <div className="cp-line-val">Same as the service address</div>}
              </div>
            </div>
            <div className="cp-line">
              <div className="cp-line-main">
                <div className="cp-line-lbl">Portal access</div>
                <div className="cp-line-val">
                  {client.email
                    ? 'They can sign in with their email'
                    : 'No email on file, so there is nowhere to send a set-password link'}
                </div>
              </div>
            </div>
            <div className="cp-line">
              <div className="cp-line-main">
                <div className="cp-line-lbl">Jobs on file</div>
                <div className="cp-line-val">
                  {theirRequests.length} {theirRequests.length === 1 ? 'request' : 'requests'}
                  {theirProjects.length ? ` · ${theirProjects.length} project${theirProjects.length === 1 ? '' : 's'}` : ''}
                </div>
              </div>
            </div>
            <div className="cp-line">
              <div className="cp-line-main">
                <div className="cp-line-lbl">Client since</div>
                <div className="cp-line-val">{client.createdAt ? fmtDate(client.createdAt) : '—'}</div>
              </div>
            </div>
            <div className="cp-line">
              <div className="cp-line-main">
                <div className="cp-line-lbl">Notes</div>
                <div className={`cp-line-val${client.notes ? '' : ' empty'}`}>{client.notes || 'None'}</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
